import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Eyebrow, SectionNum } from "@/components/ui-bits";
import api from "@/lib/api";
import { ArrowUpRight } from "lucide-react";

function formatDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }); }
  catch { return ""; }
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/posts")
      .then(({ data }) => setPosts(data.filter((p) => p.category !== "News")))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Layout>
      <SEO
        title="Insights"
        description="Longform legal commentary from Shelton Law PLLC on business transactions, intellectual property, international trade, export compliance, CFIUS, and related federal regulatory matters."
        path="/blog"
      />
      <section className="border-b border-hairline" data-testid="blog-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-16">
          <Eyebrow>Insights</Eyebrow>
          <h1 className="display-1 text-ink mt-6 max-w-4xl">
            Notes from the practice.
          </h1>
          <p className="mt-8 text-ink-soft text-[1.0625rem] leading-relaxed max-w-2xl">
            Commentary on developments and recurring questions in the firm&apos;s practice areas — business transactions, intellectual property, international trade, export compliance, CFIUS, and related federal regulatory matters. For firm announcements and press, see News.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20" data-testid="blog-list">
        {loading && <p className="text-ink-soft" data-testid="blog-loading">Loading…</p>}
        {!loading && posts.length === 0 && (
          <div className="border-t border-hairline pt-10" data-testid="blog-empty">
            <p className="eyebrow">Forthcoming</p>
            <p className="font-serif text-3xl text-ink mt-4 leading-snug max-w-2xl">
              New writing is being prepared for publication.
            </p>
            <p className="text-ink-soft text-[1.0625rem] leading-relaxed max-w-2xl mt-5">
              In the meantime, the Guides page offers downloadable reference materials on a number of the topics that come up most often in the practice.
            </p>
            <div className="mt-8">
              <Link
                to="/resources"
                className="inline-flex items-center gap-2 text-ink border-b border-brass pb-1 hover:text-brass transition-colors text-[11px] tracking-[0.24em] uppercase font-semibold"
                data-testid="blog-empty-resources-link"
              >
                Visit Guides <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
              </Link>
            </div>
          </div>
        )}

        {!loading && posts.length > 0 && (
          <ul className="border-t border-hairline">
            {posts.map((p, i) => (
              <li key={p.id} className="border-b border-hairline">
                <Link
                  to={`/blog/${p.slug}`}
                  className="group grid grid-cols-12 gap-6 py-8 lg:py-10 items-baseline hover:bg-ivory-alt transition-colors -mx-3 px-3"
                  data-testid={`blog-card-${p.slug}`}
                >
                  <SectionNum className="col-span-2 sm:col-span-1 self-start pt-3">{String(i + 1).padStart(2, "0")}</SectionNum>
                  <div className="col-span-10 sm:col-span-8">
                    <p className="eyebrow">{p.category}</p>
                    <h3 className="font-serif text-2xl sm:text-3xl text-ink leading-tight mt-3 group-hover:text-brass transition-colors">{p.title}</h3>
                    <p className="text-ink-soft text-sm leading-relaxed mt-3 max-w-2xl">{p.excerpt}</p>
                  </div>
                  <div className="hidden sm:flex col-span-3 justify-end items-baseline gap-3">
                    <ArrowUpRight className="w-4 h-4 text-brass opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </Layout>
  );
}
